import {
  browserTracingIntegration,
  captureConsoleIntegration,
  replayIntegration,
} from "@sentry/browser";
import * as Sentry from "@sentry/react";

// SENTRY INITIALIZATION AND CONFIGURATION
export function initSentry() {
  // Detect window.Cypress, we won't initialize Sentry
  // if we are running E2E tests in Cypress
  const isCypress = "Cypress" in window;
  if (isCypress) return;

  // If not on prod/test environments OR
  // local not set to run sentry, we also won't initialize Sentry
  const SENTRY_ENABLED_STAGES = ["prod", "test"];
  const isEnabledStage = SENTRY_ENABLED_STAGES.includes(import.meta.env.VITE_STAGE);
  const isLocalWithSentry =
    import.meta.env.VITE_STAGE === "local" && import.meta.env.VITE_SENTRY_RUN_LOCAL === "true";
  if (!(isEnabledStage || isLocalWithSentry)) return;

  // All checks done, Initialize Sentry
  Sentry.init({
    dsn: import.meta.env.VITE_STOREFRONT_CHECKOUT_SENTRY_DSN,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    environment: import.meta.env.VITE_SENTRY_ENV,
    ignoreErrors: [
      "Your card has insufficient funds",
      "Your card was declined",
      "NotAuthorizedException: User is disabled",
      "Your card number is incorrect",
      "Your card could not be authorized using the postal code provided",
    ],
    denyUrls: [
      // Google Adsense
      /pagead\/js/i,
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
    ],
    tracesSampleRate: import.meta.env.VITE_STAGE === "prod" ? 0.2 : 1.0,
    normalizeDepth: 3,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      captureConsoleIntegration({ levels: ["error"] }),
      browserTracingIntegration(),
      replayIntegration(),
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : typeof (error as { message?: unknown } | undefined)?.message === "string"
              ? (error as { message: string }).message
              : undefined;
      // Create a custom fingerprint for errors
      // This will help separete errors by environment and release
      if (error && event.exception?.values?.length) {
        const env = import.meta.env.VITE_SENTRY_ENV;
        const release = import.meta.env.VITE_SENTRY_RELEASE;
        const errorName =
          error instanceof Error
            ? error.name
            : typeof (error as { name?: unknown } | undefined)?.name === "string"
              ? (error as { name: string }).name
              : "Error";

        const fingerprint = [errorName, errorMessage || "unknown", env, release];
        event.fingerprint = fingerprint;
      }
      return event; // Send all other events as usual
    },
    sendDefaultPii: false,
  });
}
